import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import GameShell from "../../components/Kids/GameShell";
import Pill from "../../components/Kids/Pill";
import Button from "../../components/Kids/Button";
import EmptyState from "../../components/Kids/EmptyState";
import Skeleton from "../../components/Kids/Skeleton";

const API   = process.env.REACT_APP_API_URL;
const token = () => localStorage.getItem("kids_parent_token");
const WATCH_BG = "linear-gradient(160deg,#FFF8F2 0%,#FFE9D6 45%,#FFD9BF 100%)";

/* Load the YouTube IFrame API once and resolve when ready. */
function loadYT() {
  return new Promise((resolve) => {
    if (window.YT && window.YT.Player) return resolve(window.YT);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => { if (prev) prev(); resolve(window.YT); };
    if (!document.getElementById("yt-iframe-api")) {
      const s = document.createElement("script");
      s.id = "yt-iframe-api";
      s.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(s);
    }
  });
}

export default function KidsWatch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const childId  = params.get("childId") || sessionStorage.getItem("childId");

  const [zone, setZone]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    if (!childId) { navigate("/kids/login"); return; }
    setLoading(true);
    axios.get(`${API}/api/kids/watch/${childId}/zone`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => setZone(r.data.data))
      .catch(() => setZone(null))
      .finally(() => setLoading(false));
  }, [childId, navigate]);

  useEffect(() => { load(); }, [load]);

  const goGames = () => navigate(`/kids/games?childId=${childId}`);

  if (loading) return (
    <GameShell title={t("kids.watch.title")} emoji="📺" theme="light" bg={WATCH_BG} onBack={goGames} maxWidth={860}>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-2">
        {[0, 1, 2, 3, 4, 5].map(i => (
          <div key={i} className="bg-white rounded-card shadow-card overflow-hidden">
            <Skeleton className="w-full aspect-video" rounded="0px" />
            <div className="p-3">
              <Skeleton className="h-3.5 w-4/5 mb-2" />
              <Skeleton className="h-2.5 w-1/3" />
            </div>
          </div>
        ))}
      </div>
    </GameShell>
  );
  if (!zone) return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: WATCH_BG }}>
      <div className="bg-white rounded-card-lg shadow-card w-full max-w-[360px]">
        <EmptyState emoji="📡" title={t("kids.common.offline")} subtitle={t("kids.common.offlineDesc")} cta={t("kids.common.retry")} onCta={load} />
      </div>
    </div>
  );
  if (!zone.enabled) return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: WATCH_BG }}>
      <div className="bg-white rounded-card-lg shadow-card w-full max-w-[360px]">
        <EmptyState emoji="🔒" title="Watch Rewards are turned off" subtitle="Ask a grown-up to switch them on!" cta="Back to games" onCta={goGames} />
      </div>
    </div>
  );

  const balance = zone.balance_minutes;

  return (
    <GameShell
      title={t("kids.watch.title")}
      emoji="📺"
      theme="light"
      bg={WATCH_BG}
      onBack={goGames}
      maxWidth={860}
      right={<Pill>⏱ {balance} min</Pill>}
      showControls
    >
      {balance <= 0 ? (
        <div className="bg-white rounded-card-lg shadow-card p-8 text-center mt-6">
          <div className="text-6xl mb-3">🎮</div>
          <h1 className="text-xl text-ink mb-1 font-display font-semibold">Earn your watch time!</h1>
          <p className="text-muted text-sm mb-5">
            Finish a learning game to earn <b>{zone.minutes_per_game} minutes</b>. Then come back to watch! 🌟
          </p>
          <Button onClick={goGames}>Play a game to earn minutes 🚀</Button>
        </div>
      ) : zone.videos.length === 0 ? (
        <div className="bg-white rounded-card-lg shadow-card p-8 text-center mt-6">
          <div className="text-6xl mb-3">🍿</div>
          <p className="text-muted font-body font-semibold">{t("kids.watch.noVideos")}</p>
        </div>
      ) : (
        <>
          <p className="text-center text-muted text-sm mb-4">Pick something to watch — your timer counts down while you watch.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {zone.videos.map(v => (
              <button key={v.id} onClick={() => setSelected(v)}
                className="bg-white rounded-card shadow-card hover:shadow-sheet overflow-hidden text-start transition active:scale-95">
                <div className="aspect-video bg-cream flex items-center justify-center">
                  {v.thumbnail
                    ? <img src={v.thumbnail} alt="" className="w-full h-full object-cover" />
                    : <span className="text-4xl">{v.type === "playlist" ? "🎬" : "▶️"}</span>}
                </div>
                <div className="p-3">
                  <div className="font-body font-extrabold text-ink text-sm truncate">{v.title}</div>
                  <div className="text-xs text-faint capitalize">{v.type}</div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {selected && (
        <WatchPlayer childId={childId} video={selected} balanceMinutes={balance}
          onEnd={() => { setSelected(null); load(); }}
          onGames={goGames} />
      )}
    </GameShell>
  );
}

// Player display size — remembered per device. "large" is the only one that
// auto-requests native fullscreen on ready; smaller sizes stay windowed so
// the choice actually sticks instead of being overridden by the OS.
const PLAYER_SIZES = {
  small:  { label: "Small",  icon: "🔲", maxWidth: 480 },
  medium: { label: "Medium", icon: "◻️", maxWidth: 720 },
  large:  { label: "Large",  icon: "⬜", maxWidth: 1024 },
};

/* ── Locked player overlay with a consuming countdown — no YouTube chrome ── */
function WatchPlayer({ childId, video, balanceMinutes, onEnd, onGames }) {
  const mountRef  = useRef(null);
  const playerRef = useRef(null);
  const unsentRef = useRef(0);          // watched seconds not yet sent to the server
  const [remaining, setRemaining] = useState(balanceMinutes * 60);
  const remainingRef = useRef(remaining);
  remainingRef.current = remaining;
  const [timesUp, setTimesUp] = useState(false);
  const [blocked, setBlocked] = useState(false); // video can't be embedded / unavailable
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded]     = useState(false); // hide YouTube's "more videos" end screen
  const [size, setSize] = useState(() => localStorage.getItem("kids_watch_player_size") || "large");
  const sizeRef = useRef(size);
  sizeRef.current = size;

  const chooseSize = (s) => {
    setSize(s);
    localStorage.setItem("kids_watch_player_size", s);
  };

  // Send watched whole-minutes to the server (keeps the balance honest).
  const flush = useCallback(async (final) => {
    let secs = unsentRef.current;
    if (!final) secs = Math.floor(secs / 60) * 60;   // heartbeat: only whole minutes
    if (secs <= 0) return;
    unsentRef.current -= secs;
    try {
      await axios.post(`${API}/api/kids/watch/${childId}/consume`, { seconds: secs },
        { headers: { Authorization: `Bearer ${token()}` } });
    } catch { /* ignore — final flush on close will retry the remainder */ }
  }, [childId]);

  useEffect(() => {
    let tick, beat, cancelled = false;

    loadYT().then((YT) => {
      if (cancelled || !mountRef.current) return;
      const savedPos = parseFloat(localStorage.getItem(`watch_pos_${video.youtube_id}`) || "0");
      const playerVars = {
        rel: 0, modestbranding: 1, playsinline: 1, autoplay: 1,
        controls: 0,
        disablekb: 1,
        fs: 1,
        iv_load_policy: 3,
        origin: window.location.origin,
        start: Math.floor(savedPos),
      };
      const opts = {
        height: "100%", width: "100%", playerVars,
        events: {
          onError: () => setBlocked(true),
          onReady: () => {
            if (sizeRef.current !== "large") return; // smaller sizes stay windowed
            try {
              const el = mountRef.current?.querySelector("iframe");
              if (el?.requestFullscreen) el.requestFullscreen().catch(() => {});
            } catch {}
          },
          onStateChange: (e) => {
            setPlaying(e.data === 1);
            if (e.data === 0) { setEnded(true); flush(true); localStorage.removeItem(`watch_pos_${video.youtube_id}`); }
          },
        },
      };
      if (video.type === "playlist") { playerVars.listType = "playlist"; playerVars.list = video.youtube_id; }
      else { opts.videoId = video.youtube_id; }
      playerRef.current = new YT.Player(mountRef.current, opts);
    });

    tick = setInterval(() => {
      const p = playerRef.current;
      const playing = p && p.getPlayerState && p.getPlayerState() === 1; // 1 = playing
      if (!playing) return;
      unsentRef.current += 1;
      // Save playback position for resume
      try { const ct = p.getCurrentTime?.(); if (ct > 0) localStorage.setItem(`watch_pos_${video.youtube_id}`, String(ct)); } catch {}
      setRemaining((r) => {
        const next = r - 1;
        if (next <= 0) {
          try { p.pauseVideo(); } catch { /* ignore */ }
          setTimesUp(true);
          flush(true);
          return 0;
        }
        return next;
      });
    }, 1000);

    beat = setInterval(() => flush(false), 30000);

    return () => {
      cancelled = true;
      clearInterval(tick); clearInterval(beat);
      flush(true);
      try { playerRef.current && playerRef.current.destroy && playerRef.current.destroy(); } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video]);

  // Kids' only control: tap to play / pause (YouTube's own UI is fully blocked).
  const toggle = () => {
    const p = playerRef.current;
    if (!p || !p.getPlayerState) return;
    if (p.getPlayerState() === 1) { try { p.pauseVideo(); } catch { /* ignore */ } }
    else                          { try { p.playVideo(); } catch { /* ignore */ } }
  };

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col">
      <div className="flex items-center justify-between gap-2 px-4 py-2 sm:py-3 text-white flex-shrink-0">
        <button onClick={onEnd} className="font-body font-bold hover:text-brand-light text-sm shrink-0">✕ Close</button>
        <div className="flex items-center gap-1 rounded-full bg-white/10 p-1">
          {Object.entries(PLAYER_SIZES).map(([key, s]) => (
            <button key={key} onClick={() => chooseSize(key)} aria-label={`${s.label} screen size`} title={s.label}
              className="w-8 h-8 rounded-full flex items-center justify-center text-sm transition"
              style={{ background: size === key ? "rgba(255,255,255,.3)" : "transparent" }}>
              {s.icon}
            </button>
          ))}
        </div>
        <div className="font-display font-extrabold bg-white/15 px-3 py-1 rounded-full text-sm shrink-0">⏱ {mm}:{ss} left</div>
      </div>

      <div className="flex-1 flex items-center justify-center px-1 sm:px-3 pb-2 sm:pb-4">
        <div className="relative w-full rounded-card overflow-hidden bg-black transition-all duration-300"
          style={{ aspectRatio: "16/9", maxWidth: PLAYER_SIZES[size].maxWidth, maxHeight: "calc(100dvh - 80px)" }}>
          {/* pointer-events-none: NO click ever reaches the YouTube iframe, so its
              title, logo, share, and end-screen "more videos" links can't be tapped. */}
          <div ref={mountRef} className="w-full h-full pointer-events-none" />

          {/* Our own tap layer = the only way to play/pause */}
          {!blocked && !ended && (
            <button onClick={toggle} aria-label="Play or pause"
              className="absolute inset-0 z-10 w-full h-full flex items-center justify-center bg-transparent">
              {!playing && (
                <span className="w-16 h-16 rounded-full bg-black/55 text-white flex items-center justify-center text-3xl">▶</span>
              )}
            </button>
          )}

          {/* Title chip */}
          {!blocked && (
            <div className="absolute start-4 bottom-4 z-10 bg-black/55 text-white font-display font-semibold text-sm px-3.5 py-1.5 rounded-[10px] pointer-events-none max-w-[70%] truncate">
              {video.title}
            </div>
          )}

          {/* End screen cover — hides YouTube's related-video grid */}
          {ended && !blocked && (
            <div className="absolute inset-0 z-20 bg-ink flex items-center justify-center p-6 text-center">
              <div>
                <div className="text-5xl mb-3">🎉</div>
                <p className="text-white font-extrabold text-lg mb-1">Video finished!</p>
                <p className="text-white/70 text-sm mb-5">Pick another one to keep watching.</p>
                <Button onClick={onEnd}>← Back to videos</Button>
              </div>
            </div>
          )}

          {/* Covers YouTube's own "Watch on YouTube" error link so kids can't escape */}
          {blocked && (
            <div className="absolute inset-0 z-20 bg-ink flex items-center justify-center p-6 text-center">
              <div>
                <div className="text-5xl mb-3">😕</div>
                <p className="text-white font-extrabold text-lg mb-1">This video can't be played here</p>
                <p className="text-white/70 text-sm mb-5">Let's pick a different one!</p>
                <Button onClick={onEnd}>← Back to videos</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <p className="text-center text-white/60 font-body font-bold text-xs pb-4">
        Tap the video to play or pause · Controls are hidden for safety
      </p>

      {timesUp && (
        <div className="absolute inset-0 bg-black/85 flex items-center justify-center p-6">
          <div className="bg-white rounded-card-lg p-8 text-center max-w-sm">
            <div className="text-6xl mb-3">⏰</div>
            <h2 className="text-xl font-display font-extrabold text-ink mb-1">Time's up!</h2>
            <p className="text-muted text-sm mb-5">Finish another learning game to earn more watch minutes. 🌟</p>
            <div className="flex gap-3">
              <button onClick={onEnd} className="flex-1 bg-cream hover:bg-cardline text-ink font-bold py-3 rounded-xl">Close</button>
              <Button onClick={onGames} className="flex-1">Play a game 🎮</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
